/*
 * Copyright 2025 MadeByTatwa LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { existsSync } from 'fs';
import { join } from 'path';
import { readJsonFile } from './fs.js';
import type { OmniRunConfig } from '../types/index.js';

/**
 * Default configuration
 */
export const DEFAULT_CONFIG: OmniRunConfig = {
  defaultMode: 'dev',
  autoFix: true,
  verbose: false,
  plugins: {},
  pluginPaths: [],
};

/**
 * Load .omnirunrc.json configuration file
 */
export async function loadConfig(cwd: string): Promise<OmniRunConfig> {
  const configPath = join(cwd, '.omnirunrc.json');

  if (!existsSync(configPath)) {
    return DEFAULT_CONFIG;
  }

  try {
    const userConfig = (await readJsonFile(configPath)) as Partial<OmniRunConfig>;
    return { ...DEFAULT_CONFIG, ...userConfig };
  } catch (error) {
    console.warn(`Warning: Failed to load .omnirunrc.json: ${error instanceof Error ? error.message : String(error)}`);
    return DEFAULT_CONFIG;
  }
}

/**
 * Merge CLI options with config file
 */
export function mergeConfigWithOptions(
  config: OmniRunConfig,
  options: {
    verbose?: boolean;
    dryRun?: boolean;
    explain?: boolean;
  }
): OmniRunConfig {
  return {
    ...config,
    verbose: options.verbose ?? config.verbose,
  };
}

